import { useLazyQuery } from "@apollo/client";
import * as Sentry from "@sentry/browser";
import axios from "axios";
import i18n from "config/i18n/i18n";
import useStore from "config/store";
import { IS_AUTH_QUERY } from "queries/neoauth/general";
import React, { createContext, useEffect, useState } from "react";
import { hotjar } from "react-hotjar";
import { useLocation, useNavigate } from "react-router-dom";
import Settings from "src/constants/appRoutes";
import minArrayObjectElement from "utils/minArrayObjectElement";
import { onErrorGraphql } from "utils/onErrorGraphql";
import LoadingPage from "views/Base/LoadingPage";
import { clearAccessToken, getAccessToken, setAccessToken } from "./authUtils";

export interface AuthContextType {
  loading: boolean;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const setUserData = useStore(state => state.setUserData);
  const resetStore = useStore(state => state.resetStore);
  const userData = useStore(state => state.userData);

  const [authChecked, setAuthChecked] = useState(false);
  const [relativeUrlPath, setRelativeUrlPath] = useState<string>("");
  const [getUserData, { loading: loadingUser, error }] = useLazyQuery<User>(IS_AUTH_QUERY);

  const fetchUser = async () => {
    const { data } = await getUserData();
    if (data?.getUserByToken) {
      const user = data.getUserByToken;
      setUserData({ ...user, groupDefault: minArrayObjectElement(user.groups, "order")! });

      // Sentry
      Sentry.setUser({ email: user.email, id: user.id });

      // Hotjar
      if (user.isNewraiUser && import.meta.env.NODE_ENV === "production") {
        hotjar.identify(user.id as string, {
          first_name: user.firstName,
          last_name: user.lastName,
          email: user.email,
        });
        hotjar.event("newrai_user");
      }

      // Language
      const lang = data.getUserByToken.language.toLowerCase();
      i18n.changeLanguage(lang);
      localStorage.setItem("language", lang);

      // Redirect
      if (relativeUrlPath) {
        navigate(relativeUrlPath, { replace: true });
      }
    } else {
      logout();
    }
  };

  if (error) {
    return onErrorGraphql(error);
  }

  const logout = async () => {
    const token = getAccessToken();

    await axios(Settings.revokeToken, {
      method: "post",
      data: `token=${token}&client_id=${Settings.clientId}`,
    });

    clearAccessToken();
    resetStore();
    window.location.href = Settings.fullLoginUrl;
  };

  const initAuth = async () => {
    const hash = location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get("access_token");
      const expiresIn = Number(params.get("expires_in"));

      if (token && expiresIn) {
        setAccessToken(token, expiresIn);
        navigate(location.pathname, { replace: true });
      }
    }

    const token = getAccessToken();
    const relUrl = new URLSearchParams(location.search).get("relative_url_path");
    if (relUrl) setRelativeUrlPath(relUrl);

    if (token && !userData) {
      await fetchUser();
    } else if (!token) {
      window.location.href = Settings.fullLoginUrl;
      return;
    }
    setAuthChecked(true);
  };

  useEffect(() => {
    initAuth();
  }, []);

  if (!authChecked || loadingUser) return <LoadingPage />;

  return (
    <AuthContext.Provider value={{ loading: loadingUser, logout }}>{children}</AuthContext.Provider>
  );
};
